import 'src/global.css';

import { useEffect } from 'react';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { usePathname } from 'src/routes/hooks';

import { BillingProvider } from 'src/billing';
import RootModal from 'src/store/modals/RootModal';
import { themeConfig, ThemeProvider } from 'src/theme';
import { NotificationsProvider } from 'src/notifications';

import { Snackbar } from 'src/components/snackbar';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { ProductFruitsInit } from 'src/components/product-fruits';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { CreditPurchaseModal } from 'src/sections/settings/billing/components/CreditPurchaseModal';
import { CreditPurchaseProvider } from 'src/sections/settings/billing/context/CreditPurchaseContext';
import { NotificationDismissalProvider } from 'src/sections/settings/billing/context/NotificationDismissalContext';

import { AuthProvider } from 'src/auth/context/auth0';

import { OrganisationProvider } from './organisation';
import { HRAuthProvider } from './auth/context/hrcustomauth/hr-provider';
import { SpaAnalyticsListener } from './observability/spa-analytics-listener';
import { UpdateAvailableSnackbar } from './observability/update-version-check';

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();

  return (
    <AuthProvider>
      <HRAuthProvider>
        <OrganisationProvider>
          <BillingProvider>
            <SettingsProvider defaultSettings={defaultSettings}>
              <NotificationsProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <ThemeProvider
                    noSsr
                    defaultMode={themeConfig.defaultMode}
                    modeStorageKey={themeConfig.modeStorageKey}
                  >
                    <MotionLazy>
                      <Snackbar />
                      <ProgressBar />
                      <SettingsDrawer defaultSettings={defaultSettings} />
                      <ProductFruitsInit />
                      <NotificationDismissalProvider>
                        <CreditPurchaseProvider>
                          <SpaAnalyticsListener />
                          {children}
                          <UpdateAvailableSnackbar />
                          <RootModal />
                          <CreditPurchaseModal />
                        </CreditPurchaseProvider>
                      </NotificationDismissalProvider>
                    </MotionLazy>
                  </ThemeProvider>
                </LocalizationProvider>
              </NotificationsProvider>
            </SettingsProvider>
          </BillingProvider>
        </OrganisationProvider>
      </HRAuthProvider>
    </AuthProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
